import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { SessionState } from '../../services/authServiceUAU';
import { LoadingSpinner } from '../ui/States';
import { ConnectivityStatus } from '../ui/ConnectivityStatus';
import ReporteBorradorBanner from '../reportes/ReporteBorradorBanner';
import OrdenCargaBorradorBanner from '../ordenesCarga/OrdenCargaBorradorBanner';
import { useTranslation } from 'react-i18next';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requireAll?: boolean; // Si true, requiere TODOS los permisos. Si false, requiere AL MENOS UNO
}

/**
 * Componente mejorado para proteger rutas que requieren autenticación
 * Optimizado para auth externa únicamente (auth.surdataconsultores.cl)
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requiredPermissions = [],
  requireAll = false
}) => {
  const {
    estaAutenticado,
    cargando,
    estadoSesion,
    reconectando,
    tieneAlgunPermiso,
    tieneTodosLosPermisos,
    manejarReconexion,
    cerrarSesion,
  } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Mostrar spinner mientras carga o verifica sesión
  if (cargando || estadoSesion === SessionState.CHECKING) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">
            {cargando ? 'Verificando autenticación...' : 'Validando sesión...'}
          </p>
        </div>
      </div>
    );
  }

  // Manejo específico por estado de sesión
  switch (estadoSesion) {
    case SessionState.EXPIRED:
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mx-auto h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.session_expired_title')}</h2>
            <p className="text-gray-600 mb-4">
              {t('auth.session_expired_desc')}
            </p>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t('auth.go_to_login')}
            </button>
          </div>
        </div>
      );

    case SessionState.NETWORK_ERROR:
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <ConnectivityStatus />
          <div className="text-center max-w-md mx-auto p-6 mt-12">
            <div className="mx-auto h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.connection_error')}</h2>
            <p className="text-gray-600 mb-4">
              {t('auth.connection_error_desc')}
            </p>
            <div className="space-y-2">
              <button
                onClick={manejarReconexion}
                disabled={reconectando}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {reconectando ? t('auth.reconnecting') : t('auth.retry_connection')}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                {t('auth.reload_page')}
              </button>
            </div>
          </div>
        </div>
      );

    case SessionState.INVALID:
      // Redirigir silenciosamente al login para sesiones inválidas
      return <Navigate to="/login" state={{ from: location }} replace />;

    case SessionState.VALID:
      // Continuar con las verificaciones normales
      break;

    default:
      // Estado desconocido, redirigir al login
      return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si llegamos aquí, la sesión es válida pero verificar autenticación
  if (!estaAutenticado) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar permisos si se especificaron
  if (requiredPermissions.length > 0) {
    const hasPermission = requireAll
      ? tieneTodosLosPermisos(requiredPermissions)
      : tieneAlgunPermiso(requiredPermissions);

    if (!hasPermission) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.access_denied')}</h2>
            <p className="text-gray-600 mb-4">
              {t('auth.access_denied_msg')}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {t('auth.required_permissions')}: {requiredPermissions.join(', ')}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => window.history.back()}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                {t('common.back')}
              </button>
              <button
                onClick={async () => { await cerrarSesion(); window.location.href = '/login'; }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                {t('auth.logout')}
              </button>
              <p className="text-xs text-gray-500 mt-2">
                {t('auth.contact_admin')}
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  // Renderizar el contenido con el componente de conectividad
  return (
    <>
      <ConnectivityStatus />
      <ReporteBorradorBanner />
      <OrdenCargaBorradorBanner />
      <div className={estadoSesion !== SessionState.VALID ? 'pt-10' : ''}>
        {children}
      </div>
    </>
  );
};

export default PrivateRoute;